import React, { useState, useEffect, useMemo } from 'react';
import { Plus, ChevronLeft, ChevronRight, Clock, CheckCircle, RefreshCw } from 'lucide-react';
import { taskService, type Task } from '../../../services/taskApi.ts';
import AddTaskModal from '../../../components/modals/AddTaskModal.tsx';
import { PageTracker } from '../../../components/PageTracker.tsx';

const WeeklyPlanner: React.FC = () => {
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [showAddTask, setShowAddTask] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  
  // Organize tasks by day of the week
  const tasksByDay = useMemo(() => {
    const byDay: Task[][] = Array(7).fill(null).map(() => []);
    tasks.forEach(task => {
      if (task.dueDate) {
        const taskDate = new Date(task.dueDate);
        const dayOfWeek = taskDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
        // Convert to our Monday-first format
        const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        byDay[adjustedDay].push(task);
      }
    });
    return byDay;
  }, [tasks]);
  
  const getWeekDates = (date: Date): Date[] => {
    const week: Date[] = [];
    const startDate = new Date(date);
    startDate.setDate(date.getDate() - date.getDay() + 1); // Start from Monday
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const weekDates = getWeekDates(currentWeek);

  // Load tasks when component mounts or week changes
  useEffect(() => {
    const loadWeeklyTasks = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Loading weekly tasks for:', currentWeek.toISOString());
        
        // Get tasks for the current week
        const weekTasks = await taskService.getCurrentWeekTasks(currentWeek.toISOString());
        
        console.log('Loaded weekly tasks:', weekTasks.length, 'tasks');
        console.log('Task statuses:', weekTasks.map(t => ({ id: t._id, title: t.title, status: t.status })));
        
        setTasks(weekTasks);
      } catch (err) {
        console.error('Error loading tasks:', err);
        setError('Failed to load tasks. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadWeeklyTasks();
  }, [currentWeek]);

  const handleTaskAdded = (newTask: Task) => {
    console.log('New task added:', newTask);
    // Add the new task to the state
    setTasks(prevTasks => [...prevTasks, newTask]);
  };

  const refreshTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const weekTasks = await taskService.getCurrentWeekTasks(currentWeek.toISOString());
      setTasks(weekTasks);
    } catch (err) {
      console.error('Error refreshing tasks:', err);
      setError('Failed to refresh tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeek);
    newDate.setDate(currentWeek.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(newDate);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Todo': return '📋';
      case 'InProgress': return '⏳';
      case 'Done': return '✅';
      default: return '📋';
    }
  };

  const toggleTaskStatus = async (taskId: string, currentStatus: 'Todo' | 'InProgress' | 'Done') => {
    try {
      setError(null);
      console.log(`Cycling task ${taskId} from status: ${currentStatus}`);
      
      // Cycle through statuses: Todo -> InProgress -> Done -> Todo
      const updatedTask = await taskService.cycleTaskStatus(taskId, currentStatus);
      console.log(`Task ${taskId} updated to status: ${updatedTask.status}`);
      
      // Update local state
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task._id === taskId 
            ? { ...updatedTask }
            : task
        )
      );
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status. Please try again.');
    }
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  
    // ── Design tokens (matches Workflows.jsx exactly) ────────────────────────────
const T = {
  page:        "var(--lyra-color-surface)",
  surface:     "var(--lyra-color-surface)",
  surface2:    "var(--lyra-color-surface-2)",
  surface3:    "var(--app-surface-3)",
  border:      "var(--lyra-color-border)",
  border2:     "var(--app-border-strong)",
  ink:         "var(--lyra-color-text)",
  ink2:        "var(--app-text-2)",
  ink3:        "var(--lyra-color-text-muted)",
  ink4:        "var(--app-text-3)",
  accent:      "var(--lyra-color-primary)",
  accentLight: "var(--app-primary-soft)",
  accentMid:   "var(--app-primary-soft-2)",
  accentDark:  "var(--app-primary-deep)",
  green:       "var(--lyra-color-success)",
  greenBg:     "var(--lyra-color-success-bg)",
  greenBd:     "var(--app-success-border)",
  red:         "var(--lyra-color-danger)",
  redBg:       "var(--lyra-color-danger-bg)",
  redBd:       "var(--app-danger-border)",
  amber:       "var(--lyra-color-warning)",
  amberBg:     "var(--lyra-color-warning-bg)",
  amberBd:     "var(--app-warning-border)",
};

  return (
    <div className="project-mgmt-page" style={{ minHeight: "100vh", background: T.page, fontFamily: "DM Sans, system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1600, margin: '0 auto', padding: isMobile ? '16px 12px 40px' : '28px 32px 60px' }}>
      <PageTracker pageTitle="Weekly Planner" category="project-management" />
      <div className="mb-6">
        <div style={{ fontSize: isMobile ? 19 : 22, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Weekly Planner</div>
        <p className="text-[color:var(--lyra-color-text-muted)] text-sm">
          Plan and organize your week ahead. Tasks are in sync with Task Management.
        </p>
      </div>

      {/* Temporary API Debugger */}
      {/* <div className="mb-6">
        <ApiDebugger />
      </div> */}

      <div className="mb-5" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 10 : 0 }}>
        <div className="flex items-center" style={{ gap: isMobile ? 8 : 16, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <button 
            onClick={() => navigateWeek('prev')}
            className="p-2 border border-[color:var(--app-border-strong)] rounded-lg hover:bg-[color:var(--lyra-color-surface)]"
            disabled={loading}
          >
            <ChevronLeft size={isMobile ? 18 : 20} />
          </button>
          <h2 className="font-semibold text-[color:var(--lyra-color-text)]" style={{ fontSize: isMobile ? 14 : 20 }}>
            Week of {weekDates[0].toLocaleDateString()} - {weekDates[6].toLocaleDateString()}
          </h2>
          <button 
            onClick={() => navigateWeek('next')}
            className="p-2 border border-[color:var(--app-border-strong)] rounded-lg hover:bg-[color:var(--lyra-color-surface)]"
            disabled={loading}
          >
            <ChevronRight size={isMobile ? 18 : 20} />
          </button>
        </div>
        <div className="flex items-center" style={{ gap: 8, width: isMobile ? '100%' : 'auto' }}>
          <button 
            onClick={() => setShowAddTask(true)}
            className="bg-[color:var(--lyra-color-primary)] text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-[color:var(--app-primary-deep)] disabled:opacity-50"
            disabled={loading}
            style={{ flex: isMobile ? 1 : undefined, justifyContent: 'center' }}
          >
            <Plus size={isMobile ? 18 : 20} />
            <span style={{ color: "var(--app-on-accent)" }}>Add Task</span>
          </button>
          <button 
            onClick={refreshTasks}
            className="bg-[color:var(--app-text-2)] text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-[color:var(--lyra-color-text)] disabled:opacity-50"
            disabled={loading}
            style={{ flex: isMobile ? 1 : undefined, justifyContent: 'center' }}
          >
            <RefreshCw size={isMobile ? 18 : 20} className={loading ? 'animate-spin' : ''} />
            <span style={{ color: "var(--app-on-accent)" }}>Refresh</span>
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="text-red-600 text-xs underline mt-1"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="mb-4 p-4 bg-[color:var(--app-primary-soft)] border border-[color:var(--app-primary-soft-2)] rounded-lg">
          <p className="text-[color:var(--app-primary-deep)] text-sm">Loading tasks...</p>
        </div>
      )}

      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: isMobile ? 18 : 32 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(190px, 1fr))',
            gap: isMobile ? 10 : 12,
            minWidth: isMobile ? 1540 : 1330,
          }}
        >
        {days.map((day, index) => {
          const dayTasks = (tasksByDay[index] || []);
          const dayDate = weekDates[index];
          const isCurrentDay = isToday(dayDate);
          const PRIORITY_DOT: Record<string, string> = { High: T.red, Medium: T.amber, Low: T.green };

          return (
            <div key={day} style={{
              display: 'flex', flexDirection: 'column',
              background: T.surface, borderRadius: 12,
              border: `1px solid ${isCurrentDay ? T.accentMid : T.border}`,
              boxShadow: isCurrentDay ? `0 0 0 1px ${T.accentMid}` : 'none',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '10px 12px',
                background: isCurrentDay ? T.accentLight : T.surface2,
                borderBottom: `1px solid ${isCurrentDay ? T.accentMid : T.border}`,
              }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{day}</span>
                  {dayTasks.length > 0 && (
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: isCurrentDay ? T.accentDark : T.ink3 }}>{dayTasks.length}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: isCurrentDay ? T.accentDark : T.ink3, marginTop: 1, fontWeight: isCurrentDay ? 600 : 400 }}>
                  {dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {isCurrentDay && ' · Today'}
                </div>
              </div>

              <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minHeight: 90 }}>
                {dayTasks.length === 0 ? (
                  <div style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1px dashed ${T.border}`, borderRadius: 8, padding: '14px 8px',
                    textAlign: 'center', color: T.ink4, fontSize: 11.5,
                  }}>
                    No tasks
                  </div>
                ) : (
                  dayTasks.map((task) => {
                    const done = task.status === 'Done';
                    const bg = done ? T.greenBg : task.status === 'InProgress' ? T.accentLight : T.page;
                    const bd = done ? T.greenBd : task.status === 'InProgress' ? T.accentMid : T.border;
                    return (
                      <div
                        key={task._id}
                        onClick={() => toggleTaskStatus(task._id, task.status)}
                        style={{
                          padding: '8px 9px', borderRadius: 9,
                          border: `1px solid ${bd}`, background: bg,
                          cursor: 'pointer', transition: 'transform .1s, box-shadow .1s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.06)'; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: PRIORITY_DOT[task.priority] || T.ink4, flexShrink: 0, marginTop: 5 }} title={`${task.priority} priority`} />
                          <div style={{
                            fontSize: 12.5, fontWeight: 600, flex: 1, minWidth: 0,
                            color: done ? T.ink4 : T.ink,
                            textDecoration: done ? 'line-through' : 'none',
                            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          }}>
                            {task.title}
                          </div>
                          <span style={{ fontSize: 11, flexShrink: 0 }}>{getStatusIcon(task.status)}</span>
                        </div>

                        {task.description && (
                          <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 3, marginLeft: 12, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {task.description}
                          </div>
                        )}

                        {task.estimatedTimeInHours ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 5, marginLeft: 12, fontSize: 10, color: T.ink3 }}>
                            <Clock size={9} />
                            <span>{task.estimatedTimeInHours}h</span>
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))', gap: isMobile ? 10 : 16 }}>
        <div className="bg-white rounded-lg border border-[color:var(--lyra-color-border)] p-6">
          <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)] mb-4">Week Overview</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-[color:var(--lyra-color-text-muted)]">Total Tasks:</span>
              <span className="font-medium">{tasks.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[color:var(--lyra-color-text-muted)]">Todo:</span>
              <span className="font-medium text-[color:var(--lyra-color-text-muted)]">
                {tasks.filter(t => t.status === 'Todo').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[color:var(--lyra-color-primary)]">In Progress:</span>
              <span className="font-medium text-[color:var(--lyra-color-primary)]">
                {tasks.filter(t => t.status === 'InProgress').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-600">Done:</span>
              <span className="font-medium text-green-600">
                {tasks.filter(t => t.status === 'Done').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[color:var(--lyra-color-text-muted)]">Progress:</span>
              <span className="font-medium">
                {tasks.length > 0 ? Math.round((tasks.filter(t => t.status === 'Done').length / tasks.length) * 100) : 0}%
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-[color:var(--lyra-color-border)] p-6">
          <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)] mb-4">Time Allocation</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-[color:var(--lyra-color-text-muted)]">Total Hours:</span>
              <span className="font-medium">
                {tasks.reduce((acc, task) => acc + task.estimatedTimeInHours, 0)}h
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[color:var(--lyra-color-text-muted)]">Todo Hours:</span>
              <span className="font-medium text-[color:var(--lyra-color-text-muted)]">
                {tasks.filter(t => t.status === 'Todo').reduce((acc, task) => acc + task.estimatedTimeInHours, 0)}h
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[color:var(--lyra-color-primary)]">In Progress Hours:</span>
              <span className="font-medium text-[color:var(--lyra-color-primary)]">
                {tasks.filter(t => t.status === 'InProgress').reduce((acc, task) => acc + task.estimatedTimeInHours, 0)}h
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-600">Completed Hours:</span>
              <span className="font-medium text-green-600">
                {tasks.filter(t => t.status === 'Done').reduce((acc, task) => acc + task.estimatedTimeInHours, 0)}h
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-[color:var(--lyra-color-border)] p-6">
          <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)] mb-4">Status Breakdown</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-[color:var(--lyra-color-text-muted)] flex items-center">
                <span className="mr-2">📋</span>Todo:
              </span>
              <span className="font-medium">{tasks.filter(t => t.status === 'Todo').length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[color:var(--lyra-color-primary)] flex items-center">
                <span className="mr-2">⏳</span>In Progress:
              </span>
              <span className="font-medium">{tasks.filter(t => t.status === 'InProgress').length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-600 flex items-center">
                <span className="mr-2">✅</span>Done:
              </span>
              <span className="font-medium">{tasks.filter(t => t.status === 'Done').length}</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-[color:var(--lyra-color-border)] p-6">
          <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)] mb-4">Priority Breakdown</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-red-600">High Priority:</span>
              <span className="font-medium">{tasks.filter(t => t.priority === 'High').length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-yellow-600">Medium Priority:</span>
              <span className="font-medium">{tasks.filter(t => t.priority === 'Medium').length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-600">Low Priority:</span>
              <span className="font-medium">{tasks.filter(t => t.priority === 'Low').length}</span>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddTask}
        onClose={() => setShowAddTask(false)}
        onTaskAdded={handleTaskAdded}
        mode="weekly-planner"
        currentWeekDates={weekDates}
        defaultDay={1}
      />
    </div>
  );
};

export default WeeklyPlanner;
